import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import type { Deadline } from '@/components/ui/columns-tasks';
import { normalizeTaskState } from '@/components/tasks/constants';
import { useUiChangeKindSignal } from '@/lib/uiChangeBus';

interface DeadlinesResponse {
  deadlines: Deadline[];
  count: number;
}

export type DeadlineResponse = Omit<Deadline, 'date'> & {
  date: string | null;
};

interface DeadlinesApiResponse {
  deadlines: DeadlineResponse[];
  count: number;
}

const EMPTY_DEADLINES: Deadline[] = [];

/** Map one API deadline onto a client Deadline. Null/empty/unparseable dates
 *  are skipped (return null) so a single undated synthetic cannot reject the
 *  whole SWR list. Undated rows are omitted until a follow-up adds null-date
 *  UI support; do not widen `Deadline.date` here. */
export function mapDeadlineResponse(deadline: DeadlineResponse): Deadline | null {
  if (!deadline.date) {
    return null;
  }
  const [y, m, d] = deadline.date.split('T')[0].split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  if (Number.isNaN(dateObj.getTime())) {
    console.error('Skipping deadline with invalid date:', deadline.id, deadline.date);
    return null;
  }
  return {
    ...deadline,
    date: dateObj,
    state: normalizeTaskState(deadline.state),
  };
}

export function parseDeadlinesResponse(deadlines: DeadlineResponse[]): Deadline[] {
  const mapped: Deadline[] = [];
  for (const deadline of deadlines) {
    const next = mapDeadlineResponse(deadline);
    if (next) mapped.push(next);
  }
  return mapped;
}

export function useDeadlines(matterGuid: string | undefined, options?: { includeDeleted?: boolean }) {
  const includeDeleted = options?.includeDeleted ?? false;
  const { data, error, mutate, isLoading } = useSWR<DeadlinesResponse>(
    matterGuid ? `/deadlines/${matterGuid}${includeDeleted ? '?include_deleted=true' : ''}` : null,
    async (url: string) => {
      try {
        const response = await backendClient.get<DeadlinesApiResponse>(url);

        // TODO(synthetic-deadlines): this is the data-intake point for the
        // deadlines list. Synthetic deadlines carry `field_values` (date
        // candidates with EDTF `temporal_expression` + `temporal_lower/upper`).
        // The "Table" date cell currently infers date precision
        // (single / date+time / range) client-side from these bounds. Ideally
        // normalize the candidates here (or via a shared zod schema) and source
        // the precision from the backend so we don't re-derive it in the cell.
        // Convert date strings back to Date objects with validation. A single
        // unparseable row must not blank the whole list — skip it and keep the
        // rest so the table still renders.
        return {
          deadlines: parseDeadlinesResponse(response.data.deadlines),
          count: response.data.count
        };
      } catch (err) {
        console.error('Error in useDeadlines fetcher:', err);
        throw err;
      }
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );


  const deadlineChangeSignal = useUiChangeKindSignal('deadline');
  const lastDeadlineChangeSignal = useRef(deadlineChangeSignal);

  useEffect(() => {
    if (!matterGuid || deadlineChangeSignal === lastDeadlineChangeSignal.current) {
      return;
    }
    lastDeadlineChangeSignal.current = deadlineChangeSignal;
    void mutate();
  }, [deadlineChangeSignal, matterGuid, mutate]);

  return {
    deadlines: data?.deadlines ?? EMPTY_DEADLINES,
    count: data?.count ?? 0,
    isLoading,
    isError: error,
    mutate,
  };
}
